import React, { useEffect, useMemo, useState } from "react";

import {
  Container,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  IconButton,
  Slider,
} from "@material-ui/core";

import {
  Search as SearchIcon,
  ShoppingCartOutlined,
  VisibilityOutlined,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  CheckBox,
  CheckBoxOutlineBlank,
} from "@material-ui/icons";

import Header from "../../components/Header/Header";

import {
  Product,
} from "../../components/Products/ProductCard";
import ProductDetailsModal from "../../components/Products/ProductDetailsModal";

import "./Shop.css";
import { useHistory } from "react-router-dom";

interface ShopProps {
  cartCount?: number;
  onCartClick?: () => void;
  onAddToCart?: (product: Product) => void;
}

const API_URL =
  "https://shopify.anainfo.ai/api/products/?business=69f76a635d99871fd9349c1e";

const BASE_IMAGE_URL =
  "https://shopify.anainfo.ai/";

const Shop: React.FC<ShopProps> = ({
  cartCount = 0,
  onCartClick = () => {},
  onAddToCart = () => {},
}) => {

  /* ======================================================
     STATE
  ====================================================== */

  const [products, setProducts] = useState<Product[]>([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [priceRange, setPriceRange] = useState<number[]>([
    0,
    100,
  ]);

  const [priceLimits, setPriceLimits] = useState<number[]>([
    0,
    100,
  ]);

  const [selectedCategories, setSelectedCategories] =
    useState<string[]>([]);

  const [sort, setSort] = useState("default");

  const [page, setPage] = useState(1);

  const productsPerPage = 6;

  /* ======================================================
     FETCH PRODUCTS
  ====================================================== */

  useEffect(() => {
    fetchProducts();
  }, []);

const fetchProducts = async () => {
  try {
    setLoading(true);
    setError("");

    const response = await fetch(
      "https://shopify.anainfo.ai/api/products/public?template=6a90251b61bffcca1b3cb2c6",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();

      let errorMessage = "Failed to fetch products";

      try {
        const errorData = JSON.parse(errorText);

        errorMessage =
          errorData?.message ||
          errorData?.error ||
          errorMessage;
      } catch {
        if (errorText) {
          errorMessage = errorText;
        }
      }

      throw new Error(errorMessage);
    }

    const responseData = await response.json();

    console.log("Products API response:", responseData);

    // API returns:
    // {
    //   success: true,
    //   data: [],
    //   pagination: {}
    // }

    if (
      !responseData?.success ||
      !Array.isArray(responseData?.data)
    ) {
      throw new Error("Invalid products response");
    }

    const apiProducts = responseData.data;

    const mappedProducts: Product[] = apiProducts
      .filter((product: any) => product.isActive)
      .map((product: any) => {
        const categoryName =
          product.category?.name ||
          "Uncategorized";

        // profileImage is already a complete URL
        const image =
          product.profileImage ||
          product.imageList?.[0] ||
          `https://picsum.photos/500/500?random=${product._id}`;

        return {
          id: product._id,

          name:
            product.name ||
            "Unnamed Product",

          price:
            Number(product.price) || 0,

          oldPrice:
            Number(product.mrp) >
            Number(product.price)
              ? Number(product.mrp)
              : undefined,

          image,

          categories: [categoryName],

          sale:
            Number(product.mrp) >
            Number(product.price),

          description:
            product.description || "",

          stock:
            product.stock ?? 0,
        };
      });

    console.log(
      "Mapped products:",
      mappedProducts
    );

    setProducts(mappedProducts);

    // ==============================
    // DYNAMIC PRICE RANGE
    // ==============================

    if (mappedProducts.length > 0) {
      const prices = mappedProducts.map(
        (product) => product.price
      );

      const minPrice = Math.floor(
        Math.min(...prices)
      );

      const maxPrice = Math.ceil(
        Math.max(...prices)
      );

      const finalMax =
        maxPrice > minPrice
          ? maxPrice
          : minPrice + 1;

      setPriceLimits([
        minPrice,
        finalMax,
      ]);

      setPriceRange([
        minPrice,
        finalMax,
      ]);
    }

  } catch (err: any) {
    console.error(
      "Products API error:",
      err
    );

    setError(
      err?.message ||
        "Unable to load products."
    );

  } finally {
    setLoading(false);
  }
};


  /* ======================================================
     CATEGORIES
  ====================================================== */

  const categories = useMemo(() => {

    const categoryMap: {
      [key: string]: number;
    } = {};

    products.forEach((product) => {

      product.categories.forEach(
        (category:any) => {

          categoryMap[category] =
            (categoryMap[category] || 0) + 1;

        }
      );

    });

    return Object.keys(categoryMap)
      .sort()
      .map((category) => ({
        name: category,
        count: categoryMap[category],
      }));

  }, [products]);


  /* ======================================================
     CATEGORY SELECT
  ====================================================== */

  const handleCategoryChange = (
    category: string
  ) => {

    setPage(1);

    setSelectedCategories((current) => {

      if (current.includes(category)) {

        return current.filter(
          (item) => item !== category
        );

      }

      return [
        ...current,
        category,
      ];

    });

  };


  /* ======================================================
     PRICE CHANGE
  ====================================================== */

  const handlePriceChange = (
    _event: React.ChangeEvent<{}>,
    value: number | number[]
  ) => {

    if (Array.isArray(value)) {

      setPriceRange(value);

      setPage(1);

    }

  };


  /* ======================================================
     FILTER PRODUCTS
  ====================================================== */

  const filteredProducts = useMemo(() => {

    let result = [...products];


    /* ==================================================
       SEARCH
    ================================================== */

    if (search.trim()) {

      const searchValue =
        search
          .toLowerCase()
          .trim();

      result = result.filter(
        (product) =>
          product.name
            .toLowerCase()
            .includes(searchValue)
      );

    }


    /* ==================================================
       CATEGORY
    ================================================== */

    if (
      selectedCategories.length > 0
    ) {

      result = result.filter(
        (product) =>
          product.categories.some(
            (category:any) =>
              selectedCategories.includes(
                category
              )
          )
      );

    }


    /* ==================================================
       PRICE
    ================================================== */

    result = result.filter(
      (product) =>
        product.price >=
          priceRange[0] &&
        product.price <=
          priceRange[1]
    );


    /* ==================================================
       SORT
    ================================================== */

    if (sort === "low-high") {

      result.sort(
        (a, b) =>
          a.price - b.price
      );

    }

    if (sort === "high-low") {

      result.sort(
        (a, b) =>
          b.price - a.price
      );

    }

    if (sort === "name") {

      result.sort(
        (a, b) =>
          a.name.localeCompare(
            b.name
          )
      );

    }
    if (sort === "rating") {
      // Add rating sorting when your API
      // provides product ratings.
    }

    if (sort === "popularity") {
      // Add popularity sorting when your API
      // provides popularity/order data.
    }

    if (sort === "newness") {
      // Add createdAt/date sorting when your API
      // provides product creation dates.
    }

    return result;

  }, [
    products,
    search,
    selectedCategories,
    priceRange,
    sort,
  ]);


  /* ======================================================
     PAGINATION
  ====================================================== */

  const totalPages = Math.ceil(
    filteredProducts.length /
      productsPerPage
  );

  const startIndex =
    (page - 1) *
    productsPerPage;

  const visibleProducts =
    filteredProducts.slice(
      startIndex,
      startIndex +
        productsPerPage
    );


  /* ======================================================
     RESET FILTERS
  ====================================================== */

  const resetFilters = () => {

    setSearch("");

    setPriceRange([
      priceLimits[0],
      priceLimits[1],
    ]);

    setSelectedCategories([]);

    setSort("default");

    setPage(1);

  };


  /* ======================================================
     RENDER
  ====================================================== */

  return (
    <div className="shop-page">

      {/* ==================================================
          HEADER
      ================================================== */}

      <Header
        cartCount={cartCount}
        onCartClick={onCartClick}
      />


      {/* ==================================================
          SHOP CONTENT
      ================================================== */}

      <main className="shop-main">

        <Container maxWidth="lg">

          <div className="shop-layout">


            {/* ==================================================
                SIDEBAR
            ================================================== */}

            <aside className="shop-sidebar">


              {/* ================= SEARCH ================= */}

              <div className="filter-section">

                <div className="filter-heading">

                  <Typography
                    style={{
                      fontFamily: "Nunito",
                    }}
                  >
                    Search
                  </Typography>

                  <span />

                </div>


                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search products..."
                  value={search}
                  onChange={(event) => {

                    setSearch(
                      event.target.value
                    );

                    setPage(1);

                  }}
                  InputProps={{
                    className:
                      "sidebar-search-input",
                  }}
                />


                <Button
                  fullWidth
                  variant="contained"
                  className="sidebar-search-button"
                  startIcon={
                    <SearchIcon />
                  }
                  onClick={() =>
                    setPage(1)
                  }
                >
                  SEARCH
                </Button>

              </div>


              {/* ================= PRICE ================= */}

              <div className="filter-section">

                <div className="filter-heading">

                  <Typography
                    style={{
                      fontFamily: "Nunito",
                    }}
                  >
                    Fillter By Price
                  </Typography>

                  <span />

                </div>


                <div className="price-labels">

                  <span>
                    ${priceRange[0]}
                  </span>

                  <span>
                    ${priceRange[1]}
                  </span>

                </div>


                <Slider
                  value={priceRange}
                  min={priceLimits[0]}
                  max={priceLimits[1]}
                  step={1}
                  onChange={
                    handlePriceChange
                  }
                  className="price-slider"
                  disabled={loading}
                />


                <Button
                  variant="contained"
                  className="filter-button"
                  onClick={() =>
                    setPage(1)
                  }
                  style={{
                    fontFamily: "Nunito",
                  }}
                >
                  FILTER
                </Button>

              </div>


              {/* ================= CATEGORY ================= */}

              <div className="filter-section category-section">

                <div className="filter-heading">

                  <Typography
                    style={{
                      fontFamily: "Nunito",
                    }}
                  >
                    Category
                  </Typography>

                  <span />

                </div>


                <div className="category-list">

                  {categories.map(
                    (category) => (

                      <div
                        className="category-item"
                        key={
                          category.name
                        }
                      >

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedCategories.includes(
                                category.name
                              )}
                              onChange={() =>
                                handleCategoryChange(
                                  category.name
                                )
                              }
                              color="primary"
                            />
                          }
                          label={
                            <span className="category-label">
                              {
                                category.name
                              }
                            </span>
                          }
                        />

                        <span className="category-count">
                          (
                          {
                            category.count
                          }
                          )
                        </span>

                      </div>

                    )
                  )}

                </div>

              </div>


              {/* ================= RESET ================= */}

              {(search ||
                selectedCategories.length >
                  0 ||
                priceRange[0] !==
                  priceLimits[0] ||
                priceRange[1] !==
                  priceLimits[1]) && (

                <Button
                  fullWidth
                  className="reset-filter-button"
                  onClick={
                    resetFilters
                  }
                  style={{
                    fontFamily: "Nunito",
                  }}
                >
                  RESET FILTERS
                </Button>

              )}

            </aside>


            {/* ==================================================
                PRODUCTS
            ================================================== */}

            <section className="shop-products">


              {/* ================= TOP ================= */}

              <div className="shop-products-top">

                <div className="shop-breadcrumb">

                  <Typography>
                    Home
                  </Typography>

                  <KeyboardArrowRight />

                  <Typography>
                    Products
                  </Typography>

                  <div className="breadcrumb-line" />

                </div>


                <Select
                  value={sort}
                  onChange={(event) => {
                    setSort(event.target.value as string);
                    setPage(1);
                  }}
                  IconComponent={KeyboardArrowDown}
                  className="sort-select"
                  disableUnderline
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "right",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "right",
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      className: "sort-dropdown-paper",
                    },
                  }}
                >
                  <MenuItem value="default">
                    <span>Default</span>

                    {sort === "default" ? (
                      <CheckBox className="sort-checkbox selected" />
                    ) : (
                      <CheckBoxOutlineBlank className="sort-checkbox" />
                    )}
                  </MenuItem>

                  <MenuItem value="popularity">
                    <span>Popularity</span>

                    {sort === "popularity" ? (
                      <CheckBox className="sort-checkbox selected" />
                    ) : (
                      <CheckBoxOutlineBlank className="sort-checkbox" />
                    )}
                  </MenuItem>

                  <MenuItem value="rating">
                    <span>Rating</span>

                    {sort === "rating" ? (
                      <CheckBox className="sort-checkbox selected" />
                    ) : (
                      <CheckBoxOutlineBlank className="sort-checkbox" />
                    )}
                  </MenuItem>

                  <MenuItem value="newness">
                    <span>Newness</span>

                    {sort === "newness" ? (
                      <CheckBox className="sort-checkbox selected" />
                    ) : (
                      <CheckBoxOutlineBlank className="sort-checkbox" />
                    )}
                  </MenuItem>

                  <MenuItem value="low-high">
                    <span>
                      Low<br />
                      Price
                    </span>

                    {sort === "low-high" ? (
                      <CheckBox className="sort-checkbox selected" />
                    ) : (
                      <CheckBoxOutlineBlank className="sort-checkbox" />
                    )}
                  </MenuItem>

                  <MenuItem value="high-low">
                    <span>
                      High<br />
                      Price
                    </span>

                    {sort === "high-low" ? (
                      <CheckBox className="sort-checkbox selected" />
                    ) : (
                      <CheckBoxOutlineBlank className="sort-checkbox" />
                    )}
                  </MenuItem>
                </Select>

              </div>


              {/* ================= LOADING ================= */}

              {loading && (

                <div className="no-products">

                  <Typography>
                    Loading products...
                  </Typography>

                </div>

              )}


              {/* ================= ERROR ================= */}

              {!loading &&
                error && (

                <div className="no-products">

                  <Typography
                    color="error"
                  >
                    {error}
                  </Typography>

                  <Button
                    onClick={
                      fetchProducts
                    }
                  >
                    Try Again
                  </Button>

                </div>

              )}


              {/* ================= PRODUCT GRID ================= */}

              {!loading &&
                !error &&
                visibleProducts.length >
                  0 && (

                <div className="shop-product-grid">

                  {visibleProducts.map(
                    (product) => (

                      <ShopProductCard
                        key={
                          product.id
                        }
                        product={
                          product
                        }
                        onAddToCart={
                          onAddToCart
                        }
                      />

                    )
                  )}

                </div>

              )}


              {/* ================= EMPTY ================= */}

              {!loading &&
                !error &&
                visibleProducts.length ===
                  0 && (

                <div className="no-products">

                  <Typography>
                    No products found.
                  </Typography>

                  <Button
                    onClick={
                      resetFilters
                    }
                  >
                    Clear Filters
                  </Button>

                </div>

              )}


              {/* ================= PAGINATION ================= */}

              {!loading &&
                !error &&
                totalPages > 1 && (

                <div className="shop-pagination">

                  <IconButton
                    disabled={
                      page === 1
                    }
                    onClick={() =>
                      setPage(
                        (current) =>
                          Math.max(
                            1,
                            current - 1
                          )
                      )
                    }
                  >
                    <KeyboardArrowLeft />
                  </IconButton>


                  {Array.from(
                    {
                      length:
                        totalPages,
                    },
                    (_, index) =>
                      index + 1
                  ).map(
                    (number) => (

                      <Button
                        key={
                          number
                        }
                        className={
                          page ===
                          number
                            ? "page-number active"
                            : "page-number"
                        }
                        onClick={() =>
                          setPage(
                            number
                          )
                        }
                      >
                        {
                          number
                        }
                      </Button>

                    )
                  )}


                  <IconButton
                    disabled={
                      page ===
                      totalPages
                    }
                    onClick={() =>
                      setPage(
                        (current) =>
                          Math.min(
                            totalPages,
                            current + 1
                          )
                      )
                    }
                  >
                    <KeyboardArrowRight />
                  </IconButton>

                </div>

              )}

            </section>

          </div>

        </Container>

      </main>

    </div>
  );
};


/* ==========================================================
   SHOP PRODUCT CARD
========================================================== */

interface ShopProductCardProps {
  product: Product;
  onAddToCart?: (
    product: Product
  ) => void;
}

const ShopProductCard: React.FC<
  ShopProductCardProps
> = ({
  product,
  onAddToCart,
}) => {

  const [detailsOpen, setDetailsOpen] =
    React.useState(false);

    const openProductDetails = () => {

      // Store selected product
      localStorage.setItem(
        "selectedProduct",
        JSON.stringify(product)
      );

      // Open popup
      setDetailsOpen(true);
    };

    const closeProductDetails = () => {
      setDetailsOpen(false);
    };

  return (

    <div className="shop-product-card">

      <div className="shop-product-image">

        {product.sale && (

          <div className="shop-sale-badge">
            Sale!
          </div>

        )}


        <img
          src={
            product.image
          }
          alt={
            product.name
          }
        />


        {/* Hover buttons */}

        <div className="shop-product-actions">

          <IconButton
            onClick={() =>
              onAddToCart?.(
                product
              )
            }
          >
            <ShoppingCartOutlined />
          </IconButton>

          <IconButton onClick={(event) => {
              event.stopPropagation();
              openProductDetails();
            }}>
            <VisibilityOutlined />
          </IconButton>

        </div>

      </div>


      <div className="shop-product-info">


        {/* Categories */}

        <Typography className="shop-product-category">

          {product.categories
            .slice(0, 3)
            .join(", ")}

        </Typography>


        {/* Name */}

        <Typography className="shop-product-name">

          {
            product.name
          }

        </Typography>


        {/* Rating */}

        <div className="shop-product-rating">

          <span>
            ★★★★★
          </span>

        </div>


        {/* Price */}

        <div className="shop-product-price">

          <span>
            $
            {product.price.toFixed(
              2
            )}
          </span>

          {product.oldPrice && (

            <del>
              $
              {product.oldPrice.toFixed(
                2
              )}
            </del>

          )}

        </div>

      </div>
       <ProductDetailsModal
        product={product}
        open={detailsOpen}
        onClose={closeProductDetails}
        onAddToCart={(product, quantity) => {
          console.log(
            "Add to cart:",
            product,
            quantity
          );

          onAddToCart?.(product);
        }}
      />

    </div>

  );
};

export default Shop;